import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ShieldCheck, XCircle, Search, QrCode } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { PageMasthead } from "@/components/ui-custom/PageMasthead";
import { MembershipCard } from "@/components/MembershipCard";
import { QRScannerDialog } from "@/components/QRScannerDialog";
import { extractVerifyToken } from "@/lib/qr";
import { motion } from "framer-motion";

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("ms-MY", { day: "2-digit", month: "long", year: "numeric" });
}

const STATUS_LABELS = {
  ACTIVE: "AKTIF",
  EXPIRING_SOON: "AKAN TAMAT TEMPOH",
  EXPIRED: "TAMAT TEMPOH",
  SUSPENDED: "DIGANTUNG",
  REVOKED: "DIBATALKAN",
  PENDING: "MENUNGGU",
  INACTIVE: "TIDAK AKTIF",
  BELUM_DITUNTUT: "BELUM DITUNTUT",
};

export default function Semak() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState("member_number");
  const [memberNumber, setMemberNumber] = useState("");
  const [icNumber, setIcNumber] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(searchParams.get("scan") === "1");

  const runVerify = async (params) => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await api.get("/public/verify", { params });
      setResult(res.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Ahli tidak dijumpai");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) runVerify({ token });
  }, [token]);

  const handleScanResult = (text) => {
    setScannerOpen(false);
    if (searchParams.get("scan")) {
      searchParams.delete("scan");
      setSearchParams(searchParams, { replace: true });
    }
    runVerify({ token: extractVerifyToken(text) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchMode === "member_number" && memberNumber.trim()) runVerify({ member_number: memberNumber.trim() });
    if (searchMode === "identification_number" && icNumber.trim()) runVerify({ identification_number: icNumber.trim() });
  };

  return (
    <div data-testid="semak-page">
      <PageMasthead breadcrumb="SEMAK AHLI" title="Semak Ahli PPPS" subtitle="Sahkan status keahlian menggunakan No. Ahli, No. Kad Pengenalan (contoh: 900101-13-1234) atau imbas kod QR pada kad ahli digital." />

      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {!token && (
          <>
            <div className="flex gap-2 mb-4" data-testid="semak-search-mode-toggle">
              <button
                type="button"
                onClick={() => setSearchMode("member_number")}
                data-testid="semak-mode-member-number"
                className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide border transition-colors ${searchMode === "member_number" ? "bg-piks-black text-white border-piks-black" : "bg-white text-piks-muted border-piks-border hover:border-piks-black"}`}
              >
                NO. AHLI
              </button>
              <button
                type="button"
                onClick={() => setSearchMode("identification_number")}
                data-testid="semak-mode-ic"
                className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide border transition-colors ${searchMode === "identification_number" ? "bg-piks-black text-white border-piks-black" : "bg-white text-piks-muted border-piks-border hover:border-piks-black"}`}
              >
                NO. KAD PENGENALAN
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex gap-3 mb-10">
              {searchMode === "member_number" ? (
                <Input
                  data-testid="semak-member-number-input"
                  placeholder="Masukkan No. Ahli, contoh: BGS-2024-101"
                  value={memberNumber}
                  onChange={(e) => setMemberNumber(e.target.value)}
                  className="bg-white h-12"
                />
              ) : (
                <Input
                  data-testid="semak-ic-input"
                  placeholder="Masukkan No. Kad Pengenalan, contoh: 900101-13-1234"
                  value={icNumber}
                  onChange={(e) => setIcNumber(e.target.value)}
                  className="bg-white h-12"
                />
              )}
              <Button type="submit" disabled={loading} data-testid="semak-submit-btn" className="h-12 px-6 bg-piks-black hover:bg-piks-black/90 shrink-0">
                <Search className="w-4 h-4 mr-2" /> Semak
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setScannerOpen(true)}
                data-testid="semak-scan-qr-btn"
                className="h-12 px-5 border-piks-black/20 text-piks-black hover:border-piks-black shrink-0"
              >
                <QrCode className="w-4 h-4 mr-2" /> IMBAS QR
              </Button>
            </form>
          </>
        )}

        <QRScannerDialog open={scannerOpen} onOpenChange={setScannerOpen} onResult={handleScanResult} />


        {loading && <div className="text-center py-10 text-piks-muted" data-testid="semak-loading-state">Menyemak...</div>}

        {error && !loading && (
          <div className="p-8 rounded-xl bg-red-50 border border-red-200 text-center" data-testid="semak-error-state">
            <XCircle className="w-10 h-10 text-piks-red mx-auto mb-3" />
            <p className="font-semibold text-piks-red">{error}</p>
            <p className="text-sm text-piks-muted mt-1">Sila semak semula No. Ahli anda.</p>
          </div>
        )}

        {result && !loading && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="semak-result-card">
            <div className={`p-6 text-center relative rounded-t-xl ${result.verified ? "bg-piks-black" : "bg-piks-red"}`}>
              {result.verified && (
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.span
                    className="w-16 h-16 rounded-full border-2 border-piks-gold/50"
                    animate={{ scale: [1, 1.25, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
                  />
                </motion.div>
              )}
              <div className="relative z-10">
                {result.verified ? <ShieldCheck className="w-10 h-10 text-piks-gold mx-auto mb-2" /> : <XCircle className="w-10 h-10 text-white mx-auto mb-2" />}
                <p className="text-white font-display font-extrabold text-lg tracking-wide">{result.verified ? "✓ AHLI DISAHKAN" : "KEAHLIAN TIDAK AKTIF"}</p>
              </div>
            </div>

            <div className="bg-piks-ivory border-x border-piks-border p-6 flex justify-center">
              <MembershipCard
                member={{
                  verification_token: result.verification_token,
                  profile_photo_file_id: result.profile_photo_file_id,
                  member_number: result.member_number,
                  valid_until: result.valid_until,
                  effective_status: result.status,
                  status: result.status,
                }}
                profile={{ full_name: result.full_name, passport_photo_file_id: result.passport_photo_file_id }}
                category={{ name: result.category }}
                compact
              />
            </div>

            <div className="bg-white rounded-b-xl border border-t-0 border-piks-border p-7">
              <div className="flex flex-wrap gap-2 mb-6">
                {result.badges.map((b) => (
                  <span key={b} className="text-[10px] font-semibold tracking-wide px-2.5 py-1 rounded-full bg-piks-gold/20 text-piks-black">{b}</span>
                ))}
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted">No. Ahli</span><span className="font-mono font-semibold text-piks-black" data-testid="semak-result-member-number">{result.member_number}</span></div>
                <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted">Nama</span><span className="font-semibold text-piks-black" data-testid="semak-result-name">{result.full_name}</span></div>
                <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted">Kategori</span><span className="font-semibold text-piks-black">{result.category}</span></div>
                {result.company_name && <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted">Syarikat</span><span className="font-semibold text-piks-black">{result.company_name}</span></div>}
                <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted">Status</span><span className={`font-semibold ${result.verified ? "text-emerald-600" : "text-piks-red"}`} data-testid="semak-result-status">{STATUS_LABELS[result.status] || result.status}</span></div>
                <div className="flex justify-between py-2.5"><span className="text-piks-muted">Sah Sehingga</span><span className="font-semibold text-piks-black">{formatDate(result.valid_until)}</span></div>
              </div>
            </div>
          </motion.div>
        )}
        {!result && !error && !loading && !token && (
          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            data-testid="semak-empty-state"
            className="w-full text-center py-8 text-piks-muted flex flex-col items-center gap-2 hover:text-piks-black transition-colors"
          >
            <QrCode className="w-8 h-8 opacity-40" />
            <p className="text-sm">Imbas kod QR pada kad ahli digital untuk pengesahan pantas.</p>
          </button>
        )}
      </section>
    </div>
  );
}
